<template>
  <v-container class="fill-height tw-overflow-auto" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" md="5" sm="12">
        <v-card class="mx-auto">
          <v-container>
            <v-card-title>
              <v-layout align-center justify-center>
                <v-img contain max-width="200" src="@/assets/securating_logo.png" />
              </v-layout>
            </v-card-title>

            <!-- Useful to debug the form -->
            <!--            <v-slider-->
            <!--              v-model="step"-->
            <!--              :tick-labels="[1, 2, 3, 4, 5]"-->
            <!--              max="5"-->
            <!--              min="1"-->
            <!--            />-->

            <!-- Login form -->
            <v-card-text class="tw-mt-2">
              <!-- Stepper for the company registration form at /registration/company -->
              <v-stepper v-model="step" class="elevation-0">
                <v-stepper-items>
                  <!-- Step 1 => Create the user (client) -->
                  <v-stepper-content :step="1" class="pa-0">
                    <v-card-title class="tw-p-0">{{ $t("registration.title") }}</v-card-title>

                    <Form ref="registrationUser" :callback="registrationUser">
                      <FormField
                        v-model="company.email"
                        :props="{ maxLength: 255 }"
                        i18n="registration.form.user.email"
                        input-type="email"
                        rules="required|email"
                      />

                      <FormField
                        v-model="company.user_name"
                        :props="{ maxLength: 50 }"
                        i18n="registration.form.user.user_name"
                        input-type="text"
                        rules="required"
                      />

                      <FormField
                        v-model="company.password"
                        :input-type="showPassword ? 'text' : 'password'"
                        i18n="registration.form.user.password"
                        rules="required"
                      >
                        <template #append>
                          <v-icon v-if="showPassword" small @click="showPassword = false">
                            fa-eye
                          </v-icon>
                          <v-icon v-else small @click="showPassword = true">
                            fa-eye-slash
                          </v-icon>
                        </template>
                      </FormField>

                      <div>
                        <span>{{ $t("registration.form.legal.manager.i_accept_legals") }}</span>
                        <a href="https://business.securating.com/docs/cg/cgv-securating.pdf" target="_blank">
                          {{ $t("registration.form.legal.manager.link_accept_legals") }}
                        </a>
                        <span>{{ $t("registration.form.legal.manager.i_accept_privacy") }}</span>
                        <a
                          href="https://www.securating.com/docs/charte/charte-du-respect-de-la-vie-privee.pdf"
                          target="_blank"
                        >
                          {{ $t("registration.form.legal.manager.link_accept_privacy") }}
                        </a>
                        <span>{{ $t("registration.form.legal.manager.i_accept_newsletter") }}</span>
                      </div>

                      <div class="mt-4 mb-2">
                        <router-link :to="loginUrl">
                          {{ $t("login.return") }}
                        </router-link>
                      </div>

                      <Captcha @verify="captchaResolved = true"></Captcha>

                      <v-btn
                        :disabled="!captchaResolved"
                        block
                        class="mt-4 primary"
                        @click="$refs.registrationUser.validate()"
                      >
                        {{ $t("registration.signup") }}
                      </v-btn>
                    </Form>
                  </v-stepper-content>

                  <!-- Step 2 => Add a website related to the company -->
                  <v-stepper-content :step="2" class="pa-0">
                    <v-card-title class="tw-p-0">
                      {{ $t("registration.site") }}
                    </v-card-title>

                    <Form ref="registrationSite" :callback="registrationSite">
                      <FormField
                        v-model="company.host"
                        :events="{ keydown: setTimeoutCheckDns }"
                        :props="{
                          prefix: 'https://',
                          placeholder: 'securating.com',
                          loading: checkingDns,
                        }"
                        i18n="registration.form.company.host"
                        input-type="text"
                      >
                        <template #append>
                          <v-icon v-if="validDns" color="success" small>
                            fa-check
                          </v-icon>
                          <v-icon v-else color="error" small>
                            fa-times
                          </v-icon>
                        </template>
                        <template #append-outer>
                          <Tooltip i18n="registration.help.company.host"></Tooltip>
                        </template>
                      </FormField>

                      <v-btn
                        :disabled="!validDns"
                        class="mt-4 primary"
                        block
                        @click="$refs.registrationSite.validate()"
                      >
                        {{ $t("registration.save_site") }}
                      </v-btn>
                      <v-btn
                          class="mt-4"
                          block
                          @click="$refs.registrationSite.validate()"
                      >
                        {{ $t("registration.skip_site") }}
                      </v-btn>
                    </Form>
                  </v-stepper-content>

                  <!-- Step 3 => Ask information about the contact in the company  -->
                  <v-stepper-content :step="3" class="pa-0">
                    <v-card-title class="tw-p-0">
                      {{ $t("registration.contact") }}
                    </v-card-title>

                    <Form ref="registrationCompanyContact" :callback="registrationCompanyContact">
                      <FormField
                        v-model="company.email"
                        :props="{ maxLength: 255 }"
                        i18n="registration.form.company.email"
                        input-type="email"
                        rules="required|email"
                      >
                        <template #append>
                          <Tooltip i18n="registration.help.company.email" />
                        </template>
                      </FormField>

                      <v-row>
                        <v-col>
                          <FormField
                            v-model="company.first_name"
                            :props="{ maxLength: 50 }"
                            i18n="registration.form.company.first_name"
                            input-type="text"
                          />
                        </v-col>
                        <v-col>
                          <FormField
                            v-model="company.last_name"
                            :props="{ maxLength: 50 }"
                            i18n="registration.form.company.last_name"
                            input-type="text"
                          />
                        </v-col>
                        <v-col>
                          <FormField
                            v-model="company.phone"
                            :props="{ maxLength: 15 }"
                            i18n="registration.form.company.phone"
                            input-type="tel"
                          />
                        </v-col>
                      </v-row>
                    </Form>
                    <v-btn class="mt-4 primary" block @click="$refs.registrationCompanyContact.validate()">
                      {{ $t("registration.save_contact") }}
                    </v-btn>
                  </v-stepper-content>

                  <!-- Step 4 => Asking to the client more information about his company -->
                  <v-stepper-content :step="4" class="pa-0">
                    <v-card-title class="tw-p-0">
                      {{ $t("registration.more") }}
                    </v-card-title>

                    <Form ref="registrationCompanyDetails" :callback="registrationCompanyDetails">
                      <v-row>
                        <v-col cols="6">
                          <FormField
                            v-model="company.siret"
                            :events="{ change: fetchInseeData }"
                            :props="{ maxLength: 14 }"
                            i18n="registration.form.company.siret"
                            input-type="text"
                          >
                            <template #append>
                              <Tooltip i18n="registration.help.company.siret" />
                            </template>
                          </FormField>
                        </v-col>
                        <v-col cols="6">
                          <FormField
                            v-model="company.vat_number"
                            :props="{ maxLength: 50 }"
                            i18n="registration.form.company.vat_number"
                            input-type="text"
                            not-required
                          />
                        </v-col>
                      </v-row>

                      <v-row>
                        <v-col cols="7">
                          <FormField
                            v-model="company.company_name"
                            :props="{ maxLength: 100 }"
                            i18n="registration.form.company.company_name"
                            input-type="text"
                            rules="required"
                          />
                        </v-col>
                        <v-col cols="5">
                          <FormField
                            v-model="company.employees"
                            :props="{ min: 0, max: 3000000 }"
                            i18n="registration.form.company.employees"
                            input-type="number"
                          />
                        </v-col>
                      </v-row>

                      <!-- Special field to select the company -->
                      <CategoriesField id="update_company_category" :category.sync="company.category_id">
                        <template #append-outer>
                          <Tooltip i18n="registration.help.company.category" />
                        </template>
                      </CategoriesField>

                      <FormField
                        v-model="company.street"
                        :props="{ maxLength: 100 }"
                        i18n="registration.form.company.address.street"
                        input-type="text"
                      />

                      <FormField
                        v-model="company.further_information"
                        :props="{ maxLength: 255 }"
                        i18n="registration.form.company.address.further_information"
                        input-type="text"
                      />

                      <!-- Special field to select the city from the zip code -->
                      <v-row>
                        <v-col>
                          <!-- Special field to select the country -->
                          <FormField
                            v-model="company.country"
                            :props="{ maxLength: 60 }"
                            i18n="registration.form.company.address.country"
                            input-type="text"
                          />
                        </v-col>
                        <v-col>
                          <FormField
                            v-model="company.zip"
                            :props="{ maxLength: 9 }"
                            i18n="registration.form.company.address.zip"
                            input-type="text"
                          />
                        </v-col>
                        <v-col>
                          <FormField
                            v-model="company.city"
                            :props="{ maxLength: 60 }"
                            i18n="registration.form.company.address.city"
                            input-type="text"
                          />
                        </v-col>
                      </v-row>
                    </Form>
                    <v-btn class="mt-4 primary" block @click="$refs.registrationCompanyDetails.validate()">
                      {{ $t("registration.save_more") }}
                    </v-btn>
                  </v-stepper-content>

                  <!-- Step 5 => Final step -->
                  <v-stepper-content :step="5" class="pa-0">
                    <v-card-title class="tw-p-0">
                      {{ $t("registration.end") }}
                    </v-card-title>

                    <p class="tw-mt-6" v-html="$t('registration.end_text')"></p>

                    <v-btn :to="loginUrl" class="mt-4 primary" block>
                      {{ $t("registration.login") }}
                    </v-btn>
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
            </v-card-text>
          </v-container>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import CategoriesField from "@/components/common/Form/CategoriesField"
import Captcha from "@/components/common/Form/Captcha"

export default {
  name: "Registration",
  components: {
    CategoriesField,
    Captcha,
  },
  data: () => ({
    plan: null,
    billing: null,
    loginUrl: "/login",
    step: 1,
    showPassword: false,
    captchaResolved: false,
    checkingDnsTimeout: 500,
    checkingDns: false,
    validDns: false,
    findCitiesTimeout: 500,
    cityAutoComplete: false,
    cities: [],
    timeoutHandler: null,
    token: null,
    company: {
      user_name: null,
      accept_legals: true,
      hash: null,
      email: null,
      locale: null,
      company_name: null,
      password: null,
      host: null,
      siret: null,
      vat_number: null,
      first_name: null,
      last_name: null,
      phone: null,
      employees: null,
      department: null,
      category_id: null,
      street: null,
      further_information: null,
      city: null,
      zip: null,
      country: null,
    },
  }),
  mounted() {
    this.bindQuery("plan")
    this.bindQuery("billing")

    let query = "?"

    if (this.plan !== null) {
      query += "plan=" + this.plan
    }

    if (this.billing !== null) {
      query += "&billing=" + this.billing
    }

    this.loginUrl += query

    if (this.plan !== null && this.billing !== null) {
      this.newSubscription()
    }
  },
  watch: {
    siret() {
      this.fetchInseeData()
    },
  },
  methods: {
    bindQuery(name) {
      this[name] = this.$route.query.hasOwnProperty(name) ? Number(this.$route.query[name]) : null
    },
    registrationUser() {
      return new Promise((resolve, reject) => {
        this.$axios
          .post("/office/manager/users", this.company)
          .then(response => {
            this.token = response.data.data.token
            this.step = 2
            resolve()
          })
          .catch(errors => {
            reject(errors.response.data)
          })
      })
    },
    setTimeoutCheckDns() {
      if (this.timeoutHandler !== null) {
        window.clearTimeout(this.timeoutHandler)
      }

      this.timeoutHandler = window.setTimeout(this.checkDns, this.checkingDnsTimeout)
    },
    async checkDns() {
      this.checkingDns = true
      this.validDns = false

      try {
        const result = await this.$axios.get("/office/manager/websites/check_dns", {
          params: { host: this.company.host },
        })

        this.validDns = result.data.data
      } catch (e) {
        this.validDns = false
      }

      this.checkingDns = false
    },
    registrationSite() {
      return new Promise((resolve, reject) => {
        this.$axios
          .post("/office/manager/websites", this.company, this.bearerToken())
          .then(() => {
            this.step = 3
            resolve()
          })
          .catch(errors => {
            reject(errors.response.data)
          })
      })
    },
    registrationCompanyContact() {
      return new Promise((resolve, reject) => {
        this.$axios
          .patch("/office/manager/companies/contact", this.company, this.bearerToken())
          .then(() => {
            this.step = 4
            resolve()
          })
          .catch(errors => {
            reject(errors.response.data)
          })
      })
    },
    bearerToken() {
      return {
        headers: { Authorization: `Bearer ${this.token}` },
      }
    },
    registrationCompanyDetails() {
      return new Promise((resolve, reject) => {
        this.$axios
          .patch("/office/manager/companies/details", this.company, this.bearerToken())
          .then(() => {
            this.step = 5
            resolve()
          })
          .catch(errors => {
            reject(errors.response.data)
          })
      })
    },
    fetchInseeData() {
      if (this.company.siret === null) {
        return
      }

      const len = this.company.siret.length

      if (len === 14) {
        this.$axios.get("/office/manager/companies/siret/" + this.company.siret).then(response => {
          const data = response.data.data.etablissement

          const entite = {
            company_name: data.uniteLegale.denominationUniteLegale,
            siren: data.siren,
            siret: data.siret,
            type: data.uniteLegale.categorieJuridiqueUniteLegale,
            gender: data.uniteLegale.sexeUniteLegale,
            first_name: data.uniteLegale.prenomUsuelUniteLegale,
            last_name: data.uniteLegale.nomUniteLegale,
            street: [
              data.adresseEtablissement.numeroVoieEtablissement,
              data.adresseEtablissement.typeVoieEtablissement,
              data.adresseEtablissement.libelleVoieEtablissement,
            ].join(" "),
            further_information: data.adresseEtablissement.complementAdresseEtablissement,
            city: data.adresseEtablissement.libelleCommuneEtablissement,
            zip: data.adresseEtablissement.codePostalEtablissement,
            country: "France",
          }

          this.company = {
            ...this.company,
            ...entite,
          }
        })
      }
    },
    newSubscription() {
      this.$axios.post("office/manager/subscription/init", {
        plan: this.plan,
        billing: this.billing,
      })
    },
  },
}
</script>
